import { Suspense, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { orderBy } from "es-toolkit/array"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { fetchCareProviderWithOrigin } from "@/lib/fetch-care-provider-with-origin"
import { fetchChPatient } from "@/lib/fetch-ch-patient"
import { CareProviderOpeningHoursTab } from "@/modules/care-provider/components/edit-care-provider/care-provider-opening-hours-tab"
import { CareProviderPracticeTab } from "@/modules/care-provider/components/edit-care-provider/care-provider-practice-tab"
import { HistoryEventsTable } from "@/modules/care-provider/components/edit-care-provider/history/history-events-table"
import {
  careProviderSchema,
  CareProviderSchemaType,
} from "@/modules/care-provider/components/edit-care-provider/schema/care-provider-schema"
import { useUpdateCareProviderMutation } from "@/modules/care-provider/components/edit-care-provider/services/useUpdateCareProviderMutation"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

type EditCareProviderButtonProps = {
  careProviderId: string
  disabled?: boolean
}
type EditCareProviderDialogProps = {
  onClose: VoidFunction
} & EditCareProviderButtonProps

const EditCareProviderDialogContent = ({ careProviderId, onClose }: EditCareProviderDialogProps) => {
  const { t, i18n } = useTranslation()
  const patientId = usePatientParam()
  const careProvider = useCareProviderWithOrigin(careProviderId, !careProviderId, "store-and-network")
  const [updateCareProvider, isEditing] = useUpdateCareProviderMutation()
  const [isClosedCareProvider, setIsClosedCareProvider] = useState(!careProvider?.isActive)

  const careProviderHistoryRef = orderBy(
    careProvider?.careProviderSearchHistories ?? [],
    [(history) => history.createdAt],
    ["desc"],
  )
  const updatedAt = careProvider?.lastChange?.changedAt
  const updatedBy = careProvider?.lastChange?.changedBy

  const form = useForm<CareProviderSchemaType>({
    mode: "all",
    resolver: zodResolver(careProviderSchema),
    defaultValues: {
      email: careProvider?.email,
      phoneNumber: careProvider?.phoneNumber ?? "",
      website: careProvider?.website,
      city: careProvider?.city,
      postalCode: careProvider?.postalCode,
      country: careProvider?.countryCode,
      isMedgatePartnerNetworkMember: careProvider?.isMedgatePartnerNetworkMember ?? false,
      isTakesNewPatients: careProvider?.isTakesNewPatients ?? false,
    },
  })

  const {
    handleSubmit,
    formState: { isDirty, isValidating },
  } = form

  const onSubmit = ({
    email,
    phoneNumber,
    website,
    isMedgatePartnerNetworkMember,
    isTakesNewPatients,
  }: CareProviderSchemaType) => {
    updateCareProvider({
      variables: {
        input: {
          id: careProviderId,
          email,
          phoneNumber,
          website,
          isMedgatePartnerNetworkMember,
          isTakesNewPatients,
        },
      },
      onCompleted: () => {
        const { language } = i18n
        fetchChPatient({ patientId, language })
        fetchCareProviderWithOrigin({ id: careProviderId, language })
        onClose()
      },
    })
  }

  const tabs = [
    {
      value: "generalInformation",
      component: (
        <CareProviderPracticeTab
          careProvider={careProvider}
          form={form}
          onCloseMutationCompleted={() => setIsClosedCareProvider(true)}
        />
      ),
      title: "admin.careProviders.dialog.generalInformation",
    },
    {
      value: "openingHours",
      component: <CareProviderOpeningHoursTab careProvider={careProvider} />,
      title: "admin.careProviders.dialog.openingHoursTab",
    },
    {
      value: "history",
      component: (
        <Suspense fallback={<LoadingSpinner size={24} isFullPage={true} className="my-[33%]" />}>
          <HistoryEventsTable careProviderHistoryRef={careProviderHistoryRef} />
        </Suspense>
      ),
      title: "admin.careProviders.dialog.historyTab",
    },
  ]

  return (
    <>
      <DialogHeader className="border-b">
        <DialogTitle>{t("admin.careProviders.dialog.dialogTitle")}</DialogTitle>
      </DialogHeader>
      <Tabs defaultValue="generalInformation">
        <TabsList className="mx-4 my-2" variant="secondary">
          {tabs.map(({ value, title }) => {
            return (
              <TabsTrigger variant="secondary" key={value} value={value} data-testid={value + "-tab"} asChild>
                {t(title)}
              </TabsTrigger>
            )
          })}
        </TabsList>
        {tabs.map(({ value, component }) => (
          <TabsContent className="mx-2 mt-0" value={value} key={value}>
            {component}
          </TabsContent>
        ))}
      </Tabs>
      <DialogFooter className="flex justify-end gap-2 border-t px-6 pt-4 pb-6">
        {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
        <Button onClick={onClose} variant="secondary">
          {t("common.button.close")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || isEditing || isValidating || isClosedCareProvider}>
          {t("common.button.save")}
        </Button>
      </DialogFooter>
    </>
  )
}

const EditCareProviderDialog = ({ careProviderId, onClose }: EditCareProviderDialogProps) => {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        variant="large"
        className="bg-surface shadow-5 grid w-full rounded-xl text-left align-middle opacity-100 transition-all"
        onPointerDownOutside={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}>
        <Suspense
          fallback={
            <div className="flex w-full items-center justify-center">
              <LoadingSpinner size={24} isFullPage className="my-[33%]" />
            </div>
          }>
          <EditCareProviderDialogContent careProviderId={careProviderId} onClose={onClose} />
        </Suspense>
      </DialogContentCustom>
    </Dialog>
  )
}

export { EditCareProviderDialog }
